import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Sensor } from '@models';
import { SensorReadingType } from '@models/Sensor/sensorgroup.model';
import { EditableLabel } from '@models/Labels/editableLabel.model';
import { ImageLabelProperties } from '@models/ImageFavorite/imageLabelProperties.model';
import { ImageLabelAggregations, ImageLabelType } from '@models/ImageFavorite/imageFavorite.enums';
import { SensorService } from '@services/sensor.service';
import { CommonShared } from 'app/shared/classes/common';

// Floating editor for an image favorite's labels. Opened (non-blocking, so the
// user can drag labels on the image while it is open) by the image favorite
// component's "Labels" button. It is a property editor + list only — all
// persistence and on-image geometry live in the image favorite component, which
// owns the `labels` references this modal mutates and listens to the outputs below.
@Component({
  standalone: false,
  selector: 'image-label-editor-modal',
  templateUrl: './image-label-editor-modal.component.html',
  styleUrls: ['./image-label-editor-modal.component.scss']
})
export class ImageLabelEditorModalComponent implements OnInit {
  // Live list of labels (the same references the image favorite component holds).
  labels: EditableLabel[] = [];

  // Label type used when "Add" is clicked.
  newLabelType = ImageLabelType.TextArrow;

  labelTypes = [
    { value: ImageLabelType.TextArrow, label: 'Text + arrow' },
    { value: ImageLabelType.StickyArrow, label: 'Sticky note with text + arrow' },
    { value: ImageLabelType.Box, label: 'Text Box, no arrow' },
    { value: ImageLabelType.Region, label: 'Highlighted Region' }
  ];

  aggregations = ImageLabelAggregations;

  // Sensor choices for dynamic labels, and the reading types of the selected sensor.
  sensors: Sensor[] = [];
  readingTypes: SensorReadingType[] = [];

  // The label currently being edited (a reference into `labels`).
  selected: EditableLabel | null = null;

  // Working style fields for the selected label (parsed from its properties bag).
  props: ImageLabelProperties = {};

  modalDragOn = false;

  private common = new CommonShared();

  @Output() onAdd = new EventEmitter<number>();             // emits the new labelType
  @Output() onUpdate = new EventEmitter<EditableLabel>();  // emits the edited label
  @Output() onDelete = new EventEmitter<number>();          // emits the label id
  @Output() onClosed = new EventEmitter<void>();

  constructor(public bsModalRef: BsModalRef,
              private sensorService: SensorService,
              private toastr: ToastrService) {}

  ngOnInit(): void {
    this.sensorService.getAllSensorsWithoutTypes().subscribe(
      (results: Sensor[]) => {
        this.sensors = (results ?? []).map(s => ({ ...s, displayedSensorName: this.common.getSensorDisplayName(s) }));
        this.sensors.sort((a, b) => (a.displayedSensorName ?? '').localeCompare(b.displayedSensorName ?? ''));
      },
      () => this.toastr.error('Error occurred while retrieving sensor list'));
  }

  setDragOn(on: boolean): void {
    this.modalDragOn = on;
  }

  add(): void {
    this.onAdd.emit(Number(this.newLabelType));
  }

  select(label: EditableLabel): void {
    this.selected = label;
    this.props = this.parseProps(label);
    this.loadReadingTypes();
  }

  // After the image favorite component persists an added label it pushes the
  // refreshed list and calls this so the new label is selected for editing.
  selectById(id: number): void {
    const found = this.labels.find(l => l.id === id);
    if (found != undefined) {
      this.select(found);
    }
  }

  isSelected(label: EditableLabel): boolean {
    return this.selected != undefined && this.selected.id === label.id;
  }

  // Push the edited text / type / style back onto the selected label and notify
  // the image favorite component to persist + re-render.
  applyChanges(): void {
    if (this.selected == undefined) {
      return;
    }
    this.selected.properties = JSON.stringify(this.props ?? {});
    this.onUpdate.emit(this.selected);
  }

  onDynamicChange(): void {
    if (this.selected == undefined) {
      return;
    }
    if (this.selected.isDynamic && this.selected.aggregationType == undefined) {
      this.selected.aggregationType = 'last';
    }
    this.applyChanges();
  }

  onSensorChange(): void {
    if (this.selected == undefined) {
      return;
    }
    this.selected.sensorReadingTypeID = null;
    this.loadReadingTypes();
    this.applyChanges();
  }

  onAggregationChange(): void {
    if (this.selected == undefined) {
      return;
    }
    if (this.selected.aggregationType !== 'last' && !(this.selected.aggregationMinutes > 0)) {
      this.selected.aggregationMinutes = 60;
    }
    this.applyChanges();
  }

  delete(label: EditableLabel): void {
    this.onDelete.emit(label.id);
    if (this.selected != undefined && this.selected.id === label.id) {
      this.selected = null;
    }
  }

  close(): void {
    this.onClosed.emit();
    this.bsModalRef.hide();
  }

  get isRegionType(): boolean {
    return this.selected != undefined && this.selected.labelType === ImageLabelType.Region;
  }

  // Whether the arrow toggle applies (only the arrow text labels, Types 1 & 2).
  get hasArrow(): boolean {
    return this.selected != undefined &&
           (this.selected.labelType === ImageLabelType.TextArrow || this.selected.labelType === ImageLabelType.StickyArrow);
  }

  get needsMinutes(): boolean {
    return this.selected != undefined && this.selected.aggregationType != undefined && this.selected.aggregationType !== 'last';
  }

  labelPreview(label: EditableLabel): string {
    const t = (label.text ?? '').trim();
    return t.length > 0 ? t : this.typeName(label.labelType);
  }

  typeName(labelType: number): string {
    const found = this.labelTypes.find(t => t.value === labelType);
    return found != undefined ? found.label : ('Type ' + labelType);
  }

  private loadReadingTypes(): void {
    this.readingTypes = [];
    const sensorId = this.selected?.sensorID;
    if (sensorId == undefined) {
      return;
    }
    this.sensorService.getReadingTypesForSensors([sensorId], '').subscribe(
      (results: SensorReadingType[]) => {
        // Ignore a late response for a previously selected label/sensor.
        if (this.selected?.sensorID !== sensorId) {
          return;
        }
        this.readingTypes = results ?? [];
        this.common.sortArrayByTypeParm(this.readingTypes);
      },
      () => this.toastr.error('Error occurred while retrieving reading types'));
  }

  private parseProps(label: EditableLabel): ImageLabelProperties {
    let p: ImageLabelProperties = {};
    if (label.properties != undefined && label.properties !== '') {
      try {
        p = JSON.parse(label.properties);
      } catch {
        p = {};
      }
    }
    return {
      fontSize: p.fontSize ?? 12,
      fontColor: p.fontColor ?? '#222222',
      hasStickyBackground: p.hasStickyBackground ?? (label.labelType === ImageLabelType.StickyArrow),
      hasShadow: p.hasShadow ?? (label.labelType === ImageLabelType.StickyArrow || label.labelType === ImageLabelType.Box),
      showArrow: p.showArrow ?? (label.labelType === ImageLabelType.TextArrow || label.labelType === ImageLabelType.StickyArrow),
      regionColor: p.regionColor ?? '#ffd966',
      decimals: p.decimals ?? 1,
      units: p.units ?? '',
      valueFontSize: p.valueFontSize ?? 18
    };
  }
}
